import * as THREE from 'three';
import type { SelectableObject } from '../selectableObject';
import { placeObjectInScene } from '@/stores/basicSceneStore';
import { getActiveItemConfig } from '@/stores/itemStore';

type PlaceDragState = {
  startCorner: THREE.Vector3;
  endCorner: THREE.Vector3;
};

export class PlaceTool {
  private readonly floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly previewMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({
      color: 0xf4d35e,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    }),
  );
  private dragState: PlaceDragState | null = null;

  constructor() {
    this.previewMesh.visible = false;
    this.previewMesh.rotation.x = -Math.PI / 2;
    this.previewMesh.position.y = 0.01;
    this.previewMesh.renderOrder = 998;
  }

  render(parent: THREE.Object3D) {
    parent.add(this.previewMesh);
  }

  isDragging() {
    return this.dragState !== null;
  }

  startDrag(ray: THREE.Ray) {
    const startCorner = this.intersectFloorPlane(ray);
    if (!startCorner) {
      return false;
    }

    const snappedStartCorner = this.snapToHalfGrid(startCorner);
    const activeItem = getActiveItemConfig();

    if (activeItem.noSize) {
      const placedItem = this.createPlacedItem({
        centerX: snappedStartCorner.x,
        centerZ: snappedStartCorner.z,
      }) as SelectableObject<any>;
      placeObjectInScene(placedItem);
      return false;
    }

    this.dragState = {
      startCorner: snappedStartCorner.clone(),
      endCorner: snappedStartCorner.clone(),
    };
    this.updatePreview();
    this.previewMesh.visible = true;

    return true;
  }

  updateDrag(ray: THREE.Ray) {
    if (!this.dragState) {
      return;
    }

    const floorPoint = this.intersectFloorPlane(ray);
    if (!floorPoint) {
      return;
    }

    this.dragState.endCorner.copy(this.snapToHalfGrid(floorPoint));
    this.updatePreview();
  }

  finishDrag() {
    if (!this.dragState) {
      return;
    }

    const { startCorner, endCorner } = this.dragState;
    this.dragState = null;
    this.previewMesh.visible = false;

    const width = Math.abs(endCorner.x - startCorner.x);
    const depth = Math.abs(endCorner.z - startCorner.z);

    if (width <= 1e-3 || depth <= 1e-3) {
      return;
    }

    const centerX = (startCorner.x + endCorner.x) * 0.5;
    const centerZ = (startCorner.z + endCorner.z) * 0.5;

    const placedItem = this.createPlacedItem({
      centerX,
      centerZ,
      width,
      depth,
    }) as SelectableObject<any>;
    placeObjectInScene(placedItem);
  }

  cancelDrag() {
    this.dragState = null;
    this.previewMesh.visible = false;
  }

  dispose() {
    this.previewMesh.geometry.dispose();
    if (Array.isArray(this.previewMesh.material)) {
      this.previewMesh.material.forEach(material => material.dispose());
    } else {
      this.previewMesh.material.dispose();
    }
  }

  private intersectFloorPlane(ray: THREE.Ray) {
    return ray.intersectPlane(this.floorPlane, new THREE.Vector3());
  }

  private updatePreview() {
    if (!this.dragState) {
      this.previewMesh.visible = false;
      return;
    }

    const { startCorner, endCorner } = this.dragState;
    const width = Math.abs(endCorner.x - startCorner.x);
    const depth = Math.abs(endCorner.z - startCorner.z);
    const centerX = (startCorner.x + endCorner.x) * 0.5;
    const centerZ = (startCorner.z + endCorner.z) * 0.5;

    this.previewMesh.position.set(centerX, 0.01, centerZ);
    this.previewMesh.scale.set(Math.max(width, 1e-3), Math.max(depth, 1e-3), 1);
  }

  private snapToHalfGrid(point: THREE.Vector3) {
    return new THREE.Vector3(
      Math.round(point.x / 0.5) * 0.5,
      point.y,
      Math.round(point.z / 0.5) * 0.5,
    );
  }

  private createPlacedItem({
    centerX,
    centerZ,
    width,
    depth,
  }: {
    centerX: number;
    centerZ: number;
    width?: number;
    depth?: number;
  }) {
    const activeItem = getActiveItemConfig();

    let centerY = 0.5;

    if (activeItem.props.size) {
      if (width && 'x' in activeItem.props.size) {
        width /= activeItem.props.size.x;
      }
      if (depth && 'z' in activeItem.props.size) {
        depth /= activeItem.props.size.z;
      }
      if ('y' in activeItem.props.size) {
        centerY *= activeItem.props.size.y;
      }
    }

    return new activeItem.clazz({
      ...activeItem.props,
      ...activeItem.toolProps,
      position: {
        x: centerX,
        y: centerY,
        z: centerZ,
        ...(activeItem.toolProps.position || {}),
      },
      ...(activeItem.noSize
        ? {}
        : {
            scale: {
              x: width ?? 1,
              y: 1,
              z: depth ?? 1,
              ...(activeItem.toolProps.scale || {}),
            },
          }),
    });
  }
}
